import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SalesOrgLeadCard, SalesOrgLeadStatus } from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";

import { toast } from "@/components/ui/use-toast";

import { salesApi } from "../../client.js";
import { buildAddToCrmPayload } from "../../leads/crmPayload.js";
import { lifecycleActions } from "../../leads/leadStatus.js";
import { SALES_VERTICALS, VerticalBadge, verticalLabel } from "../VerticalBadge.js";
import { StatusPill } from "./cells.js";

/* ── Inline editors (Status + Vertical) — `editable` rows only ──── */

/** Shared error-toast for an inline list mutation. */
function mutationErrorToast(title: string, err: unknown): void {
  toast({
    title,
    description: err instanceof Error ? err.message : "Please try again.",
    variant: "destructive",
  });
}

/**
 * The Status pill as an inline menu (full Leads list only). Clicking it opens:
 *   • "★ Qualify for CRM" — push the lead INTO the CRM as a prospect (the same
 *     flow as the lead-detail button: fetch the full lead, build the payload via
 *     the SHARED `buildAddToCrmPayload`, POST add-to-crm). Offered only while the
 *     lead isn't already in the CRM (`crm_pushed_at`) and hasn't converted.
 *   • the pipeline moves from `lifecycleActions` (Mark contacted / Mark denied /
 *     Back to active / Archive).
 * Each mutation invalidates `["sales","leads"]` so the list (and its per-tab
 * counts) refresh. A converted lead never reaches here — the caller renders the
 * read-only "Customer" pill for it.
 */
export function EditableStatusCell({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["sales", "leads"] });

  const statusMut = useMutation({
    mutationFn: (status: string) => salesApi.setLeadStatus(lead.id, status),
    onSuccess: invalidate,
    onError: (err) => mutationErrorToast("Couldn't update the lead's status", err),
  });
  const crmMut = useMutation({
    mutationFn: async () => {
      // The list card is light (no contacts) — fetch the full lead so the CRM
      // prospect carries the primary contact + agent summary, exactly like the
      // lead-detail "Qualify for CRM" button.
      const detail = await salesApi.getLead(lead.id);
      return salesApi.addLeadToCrm(lead.id, buildAddToCrmPayload(detail));
    },
    onSuccess: () => {
      toast({ title: `Qualified ${lead.name} into the CRM` });
      invalidate();
    },
    onError: (err) => mutationErrorToast("Couldn't qualify this lead for the CRM", err),
  });

  const busy = statusMut.isPending || crmMut.isPending;
  const canQualify = !lead.crm_pushed_at && !lead.converted_at;
  const actions = lifecycleActions(lead.status as SalesOrgLeadStatus);

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title="Change this lead's stage — qualify it into the CRM or move it through the pipeline."
        data-testid="lead-status-trigger"
        sx={{ borderRadius: 999, "&.Mui-disabled": { opacity: 0.6 } }}
      >
        <StatusPill lead={lead} />
        <Box component="span" aria-hidden sx={{ ml: 0.375, fontSize: 9, color: "text.disabled" }}>
          {busy ? "" : "▾"}
        </Box>
        {busy ? <CircularProgress size={11} sx={{ ml: 0.5 }} /> : null}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canQualify
          ? [
              <ListSubheader key="h-crm" sx={{ lineHeight: 2.2, bgcolor: "transparent" }}>
                Move to CRM
              </ListSubheader>,
              <MenuItem
                key="qualify"
                onClick={() => {
                  close();
                  crmMut.mutate();
                }}
                sx={{ color: "primary.main", fontWeight: 600 }}
                data-testid="lead-qualify-crm"
              >
                ★ Qualify for CRM
              </MenuItem>,
              <Divider key="d-crm" />,
            ]
          : null}
        <ListSubheader sx={{ lineHeight: 2.2, bgcolor: "transparent" }}>Set status</ListSubheader>
        {actions.map((a) => (
          <MenuItem
            key={a.status}
            onClick={() => {
              close();
              statusMut.mutate(a.status);
            }}
            {...(a.danger ? { sx: { color: "error.main" } } : {})}
          >
            {a.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The Vertical badge as an inline classification editor (full Leads list only).
 * Clicking it opens a menu of {@link SALES_VERTICALS}; picking one re-classifies
 * the lead via `salesApi.setLeadVertical` and refreshes the list. The current
 * vertical is checked + disabled.
 */
export function EditableVerticalCell({ lead }: { lead: SalesOrgLeadCard }): JSX.Element {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);

  const mut = useMutation({
    mutationFn: (vertical: string) => salesApi.setLeadVertical(lead.id, vertical),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sales", "leads"] }),
    onError: (err) => mutationErrorToast("Couldn't re-classify this lead", err),
  });

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={mut.isPending}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title="Re-classify this lead's vertical."
        data-testid="lead-vertical-trigger"
        sx={{ borderRadius: 999, "&.Mui-disabled": { opacity: 0.6 } }}
      >
        <VerticalBadge vertical={lead.vertical} />
        <Box component="span" aria-hidden sx={{ ml: 0.375, fontSize: 9, color: "text.disabled" }}>
          {mut.isPending ? "" : "▾"}
        </Box>
        {mut.isPending ? <CircularProgress size={11} sx={{ ml: 0.5 }} /> : null}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <ListSubheader sx={{ lineHeight: 2.2, bgcolor: "transparent" }}>Classification</ListSubheader>
        {SALES_VERTICALS.map((v) => (
          <MenuItem
            key={v}
            selected={v === lead.vertical}
            disabled={v === lead.vertical}
            onClick={() => {
              close();
              mut.mutate(v);
            }}
            sx={{ fontSize: 13 }}
          >
            {verticalLabel(v)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
